import { fetchRequest, RequestMethod } from '@services/api/utils/utils';

export interface AdminEventQuery {
  page?: number;
  offset?: number;
  cursor?: string;
  limit?: number;
  type?: string;
  actorId?: string;
  from?: string;
  to?: string;
}

export interface AdminEvent {
  _id: string;
  type: string;
  occurredAt: string;
  createdAt: string;
  actor?: {
    type: string;
    id: string;
    email?: string;
  };
  target?: {
    type: string;
    id: string;
  };
  context?: {
    ip?: string;
    userAgent?: string;
    sessionId?: string;
  };
  metadata?: Record<string, unknown>;
  requestId?: string;
}

export interface AdminEventsPageResponse {
  items: AdminEvent[];
  total?: number;
  page?: number;
  totalPages?: number;
  hasMore: boolean;
  limit: number;
}

export interface AdminEventsCursorResponse {
  items: AdminEvent[];
  nextCursor?: string;
  hasMore: boolean;
}

export interface InventoryHealthIssue {
  id: string;
  label: string;
  count: number;
  items: Record<string, unknown>[];
  cleanable: boolean;
  cleanupDescription?: string;
}

export interface CleanupResponse {
  issueId: string;
  affected: number;
  result: string;
}

export interface InventoryHealthResponse {
  total: number;
  issues: InventoryHealthIssue[];
}

export interface MigrationEntry {
  name: string;
  description: string;
  /** `error` means the file could not be loaded at all — `description` carries the reason. */
  status: 'pending' | 'running' | 'completed' | 'error';
  result: string | null;
  ranAt: string | null;
}

export interface MigrationsResponse {
  migrations: MigrationEntry[];
}

interface RunMigrationResponse {
  name: string;
  description: string;
  status: 'completed' | 'error';
  result: string | null;
  error?: string;
}

export type WipeScope = 'ssts' | 'spaceobjects' | 'members';

export interface WipeRequest {
  scopes: WipeScope[];
  onlyEtl: boolean;
  /** The backend defaults it to `true`; the caller always says which run it wants. */
  dryRun: boolean;
}

/**
 * A simulation and a write never share a field name: `wouldDelete` cannot be
 * misread as the proof that something was deleted.
 */
export interface WipeCollectionReport {
  before: number;
  wouldDelete?: number;
  deleted?: number;
  after?: number;
}

export interface WipeReferenceReport {
  wouldUpdate?: number;
  updated?: number;
}

export interface WipeReport {
  /** Present, and true, only on a simulation. */
  dryRun?: boolean;
  env: string;
  /** The scopes actually applied — the backend adds the cascades. */
  scopes: WipeScope[];
  onlyEtl: boolean;
  preserved: {
    users: string[];
    members: string[];
    etlServiceAccount: string | null;
  };
  collections: Record<string, WipeCollectionReport>;
  references: Record<string, WipeReferenceReport>;
  wouldDelete?: number;
  deleted?: number;
}

const apiEventEndpoints = {
  admin: {
    events: (query?: AdminEventQuery): Promise<AdminEventsCursorResponse | AdminEventsPageResponse> =>
      fetchRequest<
        AdminEventsCursorResponse | AdminEventsPageResponse,
        AdminEventsCursorResponse | AdminEventsPageResponse
      >({
        endpoint: '/admin/events',
        method: RequestMethod.GET,
        query,
      }),
    inventoryHealth: (): Promise<InventoryHealthResponse> =>
      fetchRequest<InventoryHealthResponse, InventoryHealthResponse>({
        endpoint: '/admin/inventory-health',
        method: RequestMethod.GET,
      }),
    getMigrations: (): Promise<MigrationsResponse> =>
      fetchRequest<MigrationsResponse, MigrationsResponse>({
        endpoint: '/admin/migrations',
        method: RequestMethod.GET,
      }),
    runMigration: (name: string): Promise<RunMigrationResponse> =>
      fetchRequest<RunMigrationResponse, RunMigrationResponse>({
        endpoint: `/admin/migrations/${name}`,
        method: RequestMethod.POST,
      }),
    cleanupHealthIssue: (issueId: string): Promise<CleanupResponse> =>
      fetchRequest<CleanupResponse, CleanupResponse>({
        endpoint: `/admin/inventory-health/${issueId}/cleanup`,
        method: RequestMethod.POST,
      }),
    wipe: (body: WipeRequest): Promise<WipeReport> =>
      fetchRequest<WipeReport, WipeReport, WipeRequest>({
        endpoint: '/admin/wipe',
        method: RequestMethod.POST,
        body,
      }),
  },
};

export default apiEventEndpoints;
